"use client";

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { Pagination } from "../../molecules/pagination/pagination";
import type { GlowVariant } from "../../atoms/glow-indicator/glow-indicator";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import styles from "./plan-tab-body.module.css";
import { withSafeRender } from "../../atoms/safe-render";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/* Pagination greys an arrow out at either end of its page list and cannot be told
   not to; this rail WRAPS, so it is handed a middle page that is never an end.
   Constants, NOT the real card index — a plausible wrong number would be read as
   a position. The same shim grow-card-carousel's carousel-shell.client.tsx uses. */
const WRAPPING_PAGE = 1;
const WRAPPING_PAGE_COUNT = 3;

/* How long the row waits after motion stops before the invisible re-centre. A
   debounce reset on every scroll frame, so it fires once — whether the motion was
   a stepped smooth-scroll or a free swipe. */
const SETTLE_MS = 140;

/* Sub-pixel slack, so a scroll landing a hair short of a copy boundary still
   counts as having crossed it. */
const EDGE_EPSILON = 1;

/* How far a pointer must move before a press becomes a drag-scroll rather than a
   click on the card under it. grow-card-carousel's rail uses the same 5px. */
const DRAG_THRESHOLD_PX = 5;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/* The focusable controls a card holds — its CTA, the Compare box, the channel and
   legal links. A duplicate wrap copy drops these from the tab order (see the slot
   ref below) rather than being made `inert`, so they stay mouse-clickable. */
const FOCUSABLE_IN_SLOT = "a[href], button, input, select, textarea, [tabindex]";

/* A duplicate wrap copy leaves the keyboard without leaving the pointer: every
   focusable control in it takes tabindex -1, so Tab skips it while a mouse click
   still lands. (aria-hidden, set in the JSX, keeps it out of the screen-reader
   tree.) The live (announced) copy is untouched, keeping its natural tab order. */
function dropDuplicateFromTabOrder(slot: HTMLElement, announced: boolean): void {
  if (announced) {
    return;
  }
  slot.querySelectorAll<HTMLElement>(FOCUSABLE_IN_SLOT).forEach(control => {
    control.tabIndex = -1;
  });
}

/**
 * PlanTabBody — the body of a plan-card tab: a fixed detail column BESIDE a
 * SEAMLESS INFINITE row of already-rendered plan cards, with the DDS
 * `molecules/pagination` control (prev/next arrows + progress glow) centred
 * BELOW BOTH columns — the shape the HP tab-marquee V2 frame draws.
 *
 * ⚠ GENERIC ON PURPOSE about the cards. It composes whatever cards it is handed
 * as `children` and holds no knowledge of the card. It is NOT a plan-card file:
 * the plan card is the published `PlanCard`, used unchanged. `HpTabCarousel` renders
 * the cards — and, for a wrapping rail, `repeats` identical copies of them — on the
 * SERVER and hands them here as slots, so `PlanCard` never enters the client bundle.
 *
 * ⚠ THE WRAP MECHANISM (mirrors grow-card-carousel's rail): with two or more cards
 * the organism renders REPEATS (=3) copies of the card row; the reader rides the
 * MIDDLE copy with a full copy of runway to each side. `step(±1)` scrolls by ONE
 * card; once the motion settles, `rewrap()` jumps `scrollLeft` by ±one-copy-width
 * in a single frame so the identical card in the middle copy is shown — the picture
 * never moves, so Next past the last card lands on the first (and Previous past the
 * first on the last) with no visible jump. Because there is always a neighbour, the
 * arrows NEVER disable: the pagination is fed a fake middle page (see WRAPPING_PAGE).
 *
 * ⚠ THE OVERFLOW GATE: the wrap AND the pagination engage only while one copy of the
 * real cards is WIDER than the visible card column (`active = canWrap && overflows`).
 * When the cards fit — a sparse tab on a wide viewport, or a lone card — there is no
 * scroll to page through, so no pagination renders, the aria-live announcement is
 * silenced, the duplicate copies are hidden (`data-fits` on the track), and the one
 * shown copy sits pinned at the left. The gate DEFAULTS to overflow (`overflows =
 * true`) so SSR and the no-JS path show the full wrap; only a client measurement
 * that the cards fit collapses it, so no card is ever hidden from a reader with no JS.
 *
 * ⚠ Copies 0 and 2 are `aria-hidden` and drop their focusable controls from the tab
 * order (tabindex -1), so a screen reader hears each card once and keyboard Tab never
 * lands on a duplicate. On the CLIENT they are NOT `inert`, though: a multi-card
 * viewport rests showing part of a neighbouring copy (the seamless wrap needs that
 * overlap), and `inert` there swallowed every Compare-box / CTA click that fell on the
 * duplicate. The compare input is keyed on the card's VALUE (identical across copies)
 * and a CTA keeps its href, so a POINTER click on whichever copy the wrap rests in view
 * does exactly what the live card would. SSR still ships the duplicates `inert` (so the
 * no-JS / pre-hydration keyboard path is safe, and the compare store is absent there
 * anyway); hydration swaps that inert for the tabindex treatment. The re-centre keeps
 * the reader's ANNOUNCED card inside the middle copy.
 *
 * WHY A CLIENT COMPONENT: the seamless wrap is a RUNTIME-MEASUREMENT decision — the
 * per-card pitch and the live `scrollLeft` are unknown before layout — and the
 * pagination arrows drive an imperative scroll AND report their own click. Neither
 * has a zero-JavaScript form. The detail and the cards cross the boundary as
 * ALREADY-RENDERED server nodes and ship no client JS of their own. Below 1280 the
 * detail column is hidden by CSS and only the cards show.
 *
 * ⚠ INP: the raw scroll float never becomes React state. It is read straight off the
 * track; the only rendered state is `cardIndex`, which flips only when the reader
 * crosses a card boundary, so a swipe repaints nothing until the announced card
 * changes.
 */
export interface PlanTabBodyProps {
  /** The detail column, already rendered on the server (a `TabDetailPanel`).
   *  Hidden by CSS below 1280 — only the cards show there. Absent, the row runs
   *  full width with no detail column. */
  detail?: React.ReactNode;
  /** One CSS accent colour per REAL card, in card order. The detail column takes
   *  the accent of the card currently at its leading edge (`--tab-detail-accent`),
   *  so the wash follows the card beside it as the row scrolls. Absent, the detail
   *  keeps its own default. */
  detailAccents?: string[];
  /** Desktop only: lift the detail OVER the start of a full-width card row so the
   *  cards scroll UNDERNEATH it, instead of sitting beside it. Set this only when
   *  the detail is an OCCLUDING panel (Genre's amethyst wash) — a plain, bare detail
   *  has no background to hide the cards passing behind it, so they would show
   *  through. Defaults to `false` (the detail sits beside the cards). No effect
   *  below 1280, where the detail is hidden. */
  detailUnderlap?: boolean;
  /** The plan cards, already rendered on the server. For a wrapping rail this is
   *  `cardCount * repeats` cards in copy order (copy 0, then copy 1, then copy 2). */
  children: React.ReactNode;
  /** How many REAL cards one copy holds — the announcement's total and the wrap's
   *  copy width. Not the number of `children` (that is `cardCount * repeats`). */
  cardCount: number;
  /** How many identical copies of the row `children` holds: 3 for a wrapping rail
   *  (two or more cards), 1 for a lone card that cannot wrap. The organism produces
   *  the copies on the server, which is what keeps `PlanCard` off the client bundle. */
  repeats: number;
  /** Accessible name for the scrollable row and the pagination region. */
  ariaLabel: string;
  /** The pagination glow colour. Defaults to Pagination's own "blue". */
  variant?: GlowVariant;
  /** Analytics linkName for the Previous arrow. Nothing fires without it. */
  prevLinkName?: string;
  /** Analytics linkName for the Next arrow. Nothing fires without it. */
  nextLinkName?: string;
  /** Where the control sits on the page, shared by both arrows' reporting. */
  linkPosition?: string;
  /** Identifies this control in reporting, shared by both arrows. */
  linkDestination?: string;
}

function PlanTabBodyImpl({
  detail,
  detailAccents,
  detailUnderlap,
  children,
  cardCount,
  repeats,
  ariaLabel,
  variant,
  prevLinkName,
  nextLinkName,
  linkPosition,
  linkDestination
}: PlanTabBodyProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  /* Guards the one-time centring on the middle copy — a tab revealed later (its
     box goes 0 → N) measures for the first time then, not at mount. */
  const initializedRef = useRef(false);
  /* The track's clientWidth at the last centre. A WIDTH change (a viewport resize,
     a tab reveal) re-centres the middle copy; a height-only change (a lazy image,
     the aria-live line appearing) must NOT, or it would yank the reader's scroll. */
  const lastWidthRef = useRef(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  /* Mouse/pen drag-to-scroll state, held in a ref so a drag frame never
     re-renders — the scroll float is read and written straight off the track,
     exactly like the native scroll it stands in for. */
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScroll: 0,
    pointerId: -1
  });
  /* A drag that moved ends by swallowing the click it would otherwise fire on
     the card beneath the pointer (a CTA, the compare box). */
  const suppressClickRef = useRef(false);
  /* The only rendered state: the real card at the row's leading edge, for the
     screen-reader announcement. Never the raw scroll float. */
  const [cardIndex, setCardIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  /* False through SSR and the first client render, so a duplicate wrap copy ships
     `inert` and the hydrated markup matches; flipped true once mounted, which drops
     the inert in favour of the pointer-friendly tabindex treatment below. */
  const [hydrated, setHydrated] = useState(false);

  /* Structural capability: two or more cards are repeated into a wrapping rail — a
     lone card has no neighbour to wrap to (grow-card-carousel's `count > 1 ? REPEATS
     : 1`). Whether the wrap actually ENGAGES is `active`, gated on overflow below. */
  const canWrap = cardCount > 1 && repeats > 1;
  /* Does one copy of the real cards overflow the visible card column? Starts true so
     SSR and the first client paint render the full wrap (the common case, no flash);
     a client measurement that the cards fit flips it false. */
  const [overflows, setOverflows] = useState(true);
  /* The wrap and the pagination engage only when the rail CAN wrap AND the cards
     actually overflow. When they fit there is nothing to scroll, so neither shows. */
  const active = canWrap && overflows;
  /* The interactive copy: slots [middle, middle + cardCount). The middle copy stays
     announced whether or not the gate is active — when it is not, the aria-hidden
     copies are hidden by CSS and this one sits alone at the left. */
  const middle = canWrap ? cardCount : 0;

  const slots = React.Children.toArray(children);

  /* The accent of the card at the leading edge — the one beside the detail column.
     Set as a CSS variable the detail wash reads, so the wash follows that card as
     the row scrolls. `cardIndex` is already the only rendered state (it flips on a
     card-boundary crossing), so this adds no render of its own. At SSR cardIndex is
     0, so the first card's accent is in the markup — no post-hydration flash. */
  const detailAccentStyle =
    detailAccents && detailAccents.length > 0 ?
      ({ "--tab-detail-accent": detailAccents[cardIndex] ?? detailAccents[0] } as React.CSSProperties) :
      undefined;

  /* Read each call so a breakpoint that changes the card width or the row gap is
     picked up without a width prop. Zero when there is no layout (jsdom, or a
     hidden tab), which every caller treats as "not ready".
     ⚠ Measured off the MIDDLE copy's first slot (`children[middle]`), NEVER
     `firstElementChild`: when the gate collapses, the stylesheet hides the
     aria-hidden copies — slot 0 among them — and a display:none element reports
     offsetWidth 0, which latched the gate collapsed and blocked re-expansion. The
     middle copy is always shown (slot 0 for a lone card, the interactive copy when
     wrapping), so its slot always reports a real card width. The pitch is identical
     for any slot, so only WHICH slot is measured changes — not the math. */
  const measurePitch = useCallback(() => {
    const track = trackRef.current;
    const measured = track?.children[middle] as HTMLElement | undefined;
    /* ⚠ A hidden tab's card is 0 wide but its `column-gap` still reads, so the
       bare gap would pass as a pitch and the first centring would latch while
       hidden, leaving a revealed tab on inert copy 0. No card width, no layout. */
    if (!track || !measured || !measured.offsetWidth) {
      return 0;
    }
    const gap = parseFloat(getComputedStyle(track).columnGap || "0") || 0;
    return measured.offsetWidth + gap;
  }, [middle]);

  /* The overflow gate's measurement: one copy's TRUE width — `cardCount` cards with
     `cardCount - 1` gaps between them, i.e. `cardCount * pitch - gap` — against the
     visible column. Reads the live geometry each call so a breakpoint that changes
     the card width or the column flips the gate. No layout yet (jsdom, a hidden tab)
     leaves `pitch` at 0: the gate is then LEFT at its default rather than flipped, so
     "cannot measure" means "assume overflow" and the full wrap stands. */
  const measureOverflow = useCallback(() => {
    const track = trackRef.current;
    if (!canWrap || !track) {
      return;
    }
    const pitch = measurePitch();
    if (!pitch) {
      return;
    }
    const cs = getComputedStyle(track);
    const gap = parseFloat(cs.columnGap || "0") || 0;
    /* Desktop insets the row's start so the first card clears the detail panel it
       scrolls under (plan-tab-body.module.css `.track` padding-inline-start). The
       cards only ever occupy the width BEYOND that inset, so the gate measures
       against that — not the padded clientWidth, which would over-report room and
       wrongly collapse the wrap. Zero below 1280, where the panel is hidden and
       the track carries no inset. */
    const leadingInset = parseFloat(cs.paddingInlineStart || "0") || 0;
    const realCopyWidth = cardCount * pitch - gap;
    const fits = realCopyWidth <= track.clientWidth - leadingInset + EDGE_EPSILON;
    setOverflows(!fits);
  }, [canWrap, cardCount, measurePitch]);

  /* Derives the real card under the leading edge from the raw scroll — no state
     holds the float. Guarded so an unchanged index bails React out of a re-render. */
  const syncCardIndex = useCallback(() => {
    const track = trackRef.current;
    const pitch = measurePitch();
    if (!active || !track || !pitch) {
      return;
    }
    const absolute = Math.round(track.scrollLeft / pitch);
    const real = ((absolute % cardCount) + cardCount) % cardCount;
    setCardIndex(prev => (prev === real ? prev : real));
  }, [active, cardCount, measurePitch]);

  /* Slides the reader back into the middle copy WITHOUT moving the picture: the
     landing shows the identical card one copy over. Looped, so a breakpoint that
     shrinks the pitch and drops the reader several copies out still normalises in
     one call. A jump only lands cleanly on a real scroll position, so neither shift
     is taken past an edge. Guarded for jsdom (no layout). */
  const rewrap = useCallback(() => {
    const track = trackRef.current;
    const pitch = measurePitch();
    if (!track || !active || !pitch) {
      return;
    }
    const copyWidth = cardCount * pitch;
    const maxScroll = track.scrollWidth - track.clientWidth;
    let landing = track.scrollLeft;
    while (landing >= 2 * copyWidth - EDGE_EPSILON && landing - copyWidth >= 0) {
      landing -= copyWidth;
    }
    while (landing < copyWidth - EDGE_EPSILON && landing + copyWidth <= maxScroll) {
      landing += copyWidth;
    }
    if (landing !== track.scrollLeft) {
      track.scrollLeft = landing;
    }
  }, [active, cardCount, measurePitch]);

  /* Settle the rested scroll onto the nearest card so the leading card is shown
     whole and never clipped to a half. Runs only from the settle debounce — after
     motion has stopped — which is why it does not fight a gesture: this is the
     "settle in script" path CSS scroll-snap was rejected for, because mandatory snap
     rubber-bands a part-scrolled rail and proximity re-snaps mid-burst
     (design-conflicts/grow-card-carousel.mdx). A no-op once within a pixel of a
     boundary, so the smooth scroll it starts cannot re-arm itself into a loop.
     Pitch-multiple snapping lands the card flush to the track's leading inset: at
     scrollLeft = k*pitch, card k's left edge sits exactly where card 0 starts, so no
     inset offset is needed. Guarded on a genuinely scrollable row — a fitting or lone
     card sits at 0, already a boundary — and runs independent of `active`, so a
     non-wrapping overflow rail snaps too. */
  const snapToCard = useCallback(() => {
    const track = trackRef.current;
    const pitch = measurePitch();
    if (!track || !pitch) {
      return;
    }
    if (track.scrollWidth - track.clientWidth <= EDGE_EPSILON) {
      return;
    }
    const nearest = Math.round(track.scrollLeft / pitch) * pitch;
    if (Math.abs(nearest - track.scrollLeft) <= EDGE_EPSILON) {
      return;
    }
    track.scrollTo({
      left: nearest,
      behavior: prefersReducedMotion ? "auto" : "smooth"
    });
  }, [measurePitch, prefersReducedMotion]);

  /* Centres the middle copy under the reader before first paint — and again the
     first time a hidden tab is revealed with a real box. Isomorphic because the
     shell is "use client" and therefore still SERVER-RENDERED. */
  const initialize = useCallback(() => {
    const track = trackRef.current;
    if (!track || initializedRef.current) {
      return;
    }
    if (!active) {
      /* Not wrapping (a lone card) or the cards fit: no middle-copy centre. Pin the
         row to the left so the one shown copy starts at offset 0 with no scroll, and
         reset the announced card to the first — the leading card the detail reads. */
      track.scrollLeft = 0;
      setCardIndex(prev => (prev === 0 ? prev : 0));
      initializedRef.current = true;
      lastWidthRef.current = track.clientWidth;
      return;
    }
    const pitch = measurePitch();
    if (!pitch) {
      /* Still hidden or no layout — the ResizeObserver retries on reveal. */
      return;
    }
    track.scrollLeft = cardCount * pitch;
    initializedRef.current = true;
    /* Seed the width here so the ResizeObserver's first callback on observe() is a
       no-op rather than a second measureOverflow + re-centre (one forced layout). */
    lastWidthRef.current = track.clientWidth;
    syncCardIndex();
  }, [active, cardCount, measurePitch, syncCardIndex]);

  /* Re-seat the reader after a real WIDTH change (a resize or tab reveal). A change
     that does NOT flip the overflow gate leaves `initialize` guarded, so without this
     a widened row keeps a scroll where the inert copies 0/2 fill a side and their
     cards (Compare box, CTA) cannot be clicked. Gate flips are handled by the layout
     effect (its deps change with `active`); this covers the same-regime change. */
  const recenterForWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const width = track.clientWidth;
    /* A hidden tab reports 0 — don't latch it, or the reveal (0 → N) reads as "no
       change". And a no-op on an unchanged width, so a height-only resize (a lazy
       image, the aria-live line) leaves the scroll alone. */
    if (width === 0 || width === lastWidthRef.current) {
      return;
    }
    lastWidthRef.current = width;
    measureOverflow();
    const pitch = measurePitch();
    if (!active || !pitch) {
      /* Not wrapping / the cards fit / no layout: let initialize pin or centre. */
      initializedRef.current = false;
      initialize();
      return;
    }
    /* KEEP the reader on their current card where the viewport leaves room for it in
       the middle copy; else clamp to the furthest card that keeps the WHOLE window off
       the inert copies. Card-aligned, so no half card is left at the edge. (A bare
       reset to card 0 jumped the reader on every scrollbar / nav-drawer / rotation.) */
    const copyWidth = cardCount * pitch;
    const idx = ((Math.round(track.scrollLeft / pitch) % cardCount) + cardCount) % cardCount;
    const maxOffset = Math.max(0, Math.floor((copyWidth - width) / pitch));
    track.scrollLeft = copyWidth + Math.min(idx, maxOffset) * pitch;
    syncCardIndex();
  }, [active, cardCount, measureOverflow, measurePitch, initialize, syncCardIndex]);

  /* Advance by one card width, read off the first card so it stays resize-safe and
     needs no width prop. Honours prefers-reduced-motion — passing "smooth" would
     override the CSS fallback for a reader who asked the system to stop. The wrap
     is handled by rewrap() once this scroll settles. */
  const step = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current;
      const pitch = measurePitch();
      if (!track || !pitch) {
        return;
      }
      track.scrollBy({
        left: pitch * direction,
        behavior: prefersReducedMotion ? "auto" : "smooth"
      });
    },
    [measurePitch, prefersReducedMotion]
  );

  const handlePrev = useCallback(() => {
    if (prevLinkName) {
      fireLinkClick({
        linkName: prevLinkName,
        linkPosition: linkPosition || LINK_POSITION_BODY,
        linkDestination
      });
    }
    step(-1);
  }, [step, prevLinkName, linkPosition, linkDestination]);

  const handleNext = useCallback(() => {
    if (nextLinkName) {
      fireLinkClick({
        linkName: nextLinkName,
        linkPosition: linkPosition || LINK_POSITION_BODY,
        linkDestination
      });
    }
    step(1);
  }, [step, nextLinkName, linkPosition, linkDestination]);

  /* Arrow keys scroll the focused row by one card; every other key falls through
     so Tab still reaches the CTAs inside a card. */
  const onTrackKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      }
    },
    [step]
  );

  /* Mouse/pen drag-to-scroll (parity with grow-card-carousel's rail). Touch is
     left to the browser's own momentum scroll — hijacking it would be worse than
     native. The gesture only moves `scrollLeft`, so the existing scroll listener
     drives the wrap and the announcement exactly as it does for a swipe. */
  const onTrackPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    /* Clear any suppression left by a drag that ended on nothing clickable, so a
       fresh press is never swallowed. BEFORE the touch early-return: a mouse/pen
       drag that lifted off a card leaves `suppressClickRef` armed, and a later
       touch TAP would otherwise consume it and be swallowed, since the touch path
       never reached this clear. */
    suppressClickRef.current = false;
    if (!track || event.pointerType === "touch") {
      return;
    }
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      pointerId: event.pointerId
    };
  }, []);

  const onTrackPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag.active || !track) {
      return;
    }
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      /* A press that has not travelled is still a potential click — let it be,
         so a CTA inside a card still activates. */
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;
      track.dataset.dragging = "true";
      /* Capture so the drag survives the pointer leaving the track or crossing a
         card's own element. Absent in jsdom and throwing in some older webviews
         — neither should break the scroll, which works without it. */
      try {
        track.setPointerCapture?.(drag.pointerId);
      } catch {
        /* no capture — the window pointerup still ends the drag */
      }
    }
    /* Past the threshold the gesture is a scroll, not a click or a text select. */
    event.preventDefault();
    track.scrollLeft = drag.startScroll - dx;
  }, []);

  /* A drag that moved ends as a scroll; the click it would synthesise on release
     is swallowed once, in the capture phase, before it reaches a card's CTA. */
  const onTrackClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    /* Re-measure the gate, then re-centre from scratch. A re-authored list or a width
       change that recreated `initialize` lands here; resetting the guard lets the
       middle copy re-centre when the gate flips back to overflow. */
    measureOverflow();
    initializedRef.current = false;
    initialize();
  }, [measureOverflow, initialize]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }

    /* The settle must NOT snap mid-gesture: a live mouse/pen drag owns scrollLeft
       (onTrackPointerMove writes it on every move), so a smooth-scroll started while
       the pointer is held still — then one more move — would yank the rail back and
       fight the drag. While a drag is active the settle re-arms itself instead of
       snapping, and fires once the drag ends. A touch gesture sets no drag flag: it
       is native scroll, which absorbs a mid-gesture snap rather than fighting it. */
    const runSettle = () => {
      if (dragRef.current.active) {
        settleTimerRef.current = setTimeout(runSettle, SETTLE_MS);
        return;
      }
      rewrap();
      snapToCard();
      syncCardIndex();
    };

    /* Every scroll — stepped, swiped or dragged — re-derives the announced card and
       re-arms the settle. The settle fires once the motion stops (the debounce is
       reset on each frame), then re-centres. */
    const onScroll = () => {
      syncCardIndex();
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(runSettle, SETTLE_MS);
    };

    /* A viewport resize (a breakpoint change) and a tab reveal both change the
       track's WIDTH, and both re-centre the middle copy so the reader never rests on
       an inert copy — the window `resize` catches the breakpoint change; the
       ResizeObserver catches a tab revealed by `hidden → block` (which fires NO window
       resize) and any container width change. recenterForWidth re-measures the gate,
       re-centres, and no-ops when the width is unchanged. */
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recenterForWidth);

    /* Guarded for jsdom, which has no ResizeObserver. */
    const observer =
      typeof ResizeObserver !== "undefined" ?
        new ResizeObserver(() => recenterForWidth()) :
        null;
    observer?.observe(track);

    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recenterForWidth);
      observer?.disconnect();
      clearTimeout(settleTimerRef.current);
    };
  }, [syncCardIndex, rewrap, snapToCard, recenterForWidth]);

  /* A drag ends on the window, not the track: the pointer is often outside the
     row by the time it lifts. Settled once per mount — the handler reads the
     live drag ref, so it never goes stale. */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    const endDrag = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active) {
        return;
      }
      /* Only a gesture that actually moved swallows its click — a plain press
         that released in place is a real click on a card and must pass. */
      if (drag.moved) {
        suppressClickRef.current = true;
      }
      drag.active = false;
      drag.moved = false;
      delete track.dataset.dragging;
      if (track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    };
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    /* Seed from the current value (true AND false) and track changes, so a reader
       toggling the OS setting mid-session flips step()'s scroll between smooth and
       auto without a reload. */
    const onChange = () => setPrefersReducedMotion(query.matches);

    onChange();

    /* ⚠ A MediaQueryList IS NOT AN EventTarget ON SAFARI 13 AND OLDER EMBEDDED
       WEBVIEWS: `addEventListener` is absent there and calling it throws from a
       committed effect. Remove the pair when browserslist rules those out. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);

      return () => query.removeEventListener("change", onChange);
    }

    return undefined;
  }, []);

  /* Once mounted (client only), swap the duplicate copies' SSR `inert` for the
     tabindex treatment, so their Compare boxes / CTAs become mouse-clickable. One
     re-render; it runs after the first client render so hydration still matches the
     inert markup the server sent. */
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className={styles.body} data-detail-underlap={detailUnderlap ? "" : undefined}>
      <div className={styles.topRow}>
        {/* The detail column, already rendered on the server. Hidden by CSS below
            1280 (V2): only the cards show there. Rendered only when a detail was
            handed in, so a row with no detail runs full width. */}
        {detail ? (
          <div className={styles.detailCol} style={detailAccentStyle}>
            {detail}
          </div>
        ) : null}

        <div className={styles.cardsCol}>
          {/* Focusable, named and arrow-scrollable ONLY while the gate is active. A
              fitting row (or a lone card) does not scroll, so the tab stop, role and
              name drop together rather than leaving a dead tab stop that swallows the
              Arrow keys — the mirror of grow-card-carousel's rail. They flip back when
              `active` toggles at runtime. */}
          <div
            aria-label={active ? ariaLabel : undefined}
            className={styles.track}
            /* Marks the collapsed state for the stylesheet: when the cards fit, the
               aria-hidden duplicate copies become display:none, leaving the middle
               copy alone at the left. Absent on SSR and the no-JS path (overflows
               defaults true), so nothing is hidden until the client measures a fit. */
            data-fits={!active && canWrap ? "" : undefined}
            /* Pointer-drag, keyboard scroll and the click guard are all live ONLY
               while the gate is active — a fitting row has nothing to scroll, so
               they drop together rather than leaving a dead handler on it. */
            onClickCapture={active ? onTrackClickCapture : undefined}
            onKeyDown={active ? onTrackKeyDown : undefined}
            onPointerDown={active ? onTrackPointerDown : undefined}
            onPointerMove={active ? onTrackPointerMove : undefined}
            ref={trackRef}
            role={active ? "group" : undefined}
            tabIndex={active ? 0 : undefined}
          >
            {slots.map((child, slot) => {
              /* Only the middle copy is announced; the others are the same cards
                 again. A duplicate drops out of the a11y tree (aria-hidden) and the
                 keyboard tab order (its focusable controls take tabindex -1 once
                 hydrated), yet stays POINTER-clickable — the seamless wrap rests part
                 of a duplicate copy in view, and a click on its Compare box / CTA
                 must still land (the compare input is keyed on the card's value, the
                 same across copies). NOT `inert` on the client: that blocks the
                 pointer too. Pre-hydration it keeps the SSR `inert` for the no-JS
                 keyboard path (INERT_SLOT below), which the effect above then drops. */
              const announced = slot >= middle && slot < middle + cardCount;
              return (
                <div
                  aria-hidden={announced ? undefined : true}
                  className={styles.slot}
                  key={slot}
                  /* Inline, so a re-render re-drops a duplicate's controls from the
                     tab order after anything that could restore them. */
                  ref={node => {
                    if (node && hydrated) {
                      dropDuplicateFromTabOrder(node, announced);
                    }
                  }}
                  {...(!announced && !hydrated ? INERT_SLOT : {})}
                >
                  {child}
                </div>
              );
            })}
          </div>

          {/* Announces the current card to a screen reader as the row wraps, only
              while the gate is active — a fitting row does not scroll, so there is
              nothing to announce. */}
          {active ? (
            <span aria-live="polite" className="dds-sr-only" role="status">
              {`Card ${cardIndex + 1} of ${cardCount}`}
            </span>
          ) : null}
        </div>
      </div>

      {/* The DDS Pagination molecule owns the prev/next arrows and the progress glow.
          Centred across BOTH columns (V2), not under the cards alone: the row is a
          separate block below the two-column row, and `.pagination` clamps to the
          frame's width and centres in it. Rendered ONLY while the gate is active —
          when the cards fit (or a lone card cannot wrap) there is no scroll to page
          through, so the control is absent rather than present-but-disabled. The rail
          wraps while active, so the arrows are fed a fake middle page and never grey
          out. The glow bar itself is decorative (see glow-indicator). handlePrev/
          handleNext report a `link_click` when the organism passes prev/nextLinkName
          (it does — the arrows are real <button>s, so no GTM anchor trigger
          double-counts); without those names the report is a no-op. */}
      {active ? (
        <div className={styles.paginationRow}>
          <div className={styles.pagination}>
            <Pagination
              ariaLabel={ariaLabel}
              currentPage={WRAPPING_PAGE}
              disableNext={false}
              disablePrevious={false}
              nextLabel="Next card"
              onNext={handleNext}
              onPrevious={handlePrev}
              previousLabel="Previous card"
              totalPages={WRAPPING_PAGE_COUNT}
              variant={variant}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
export const PlanTabBody = withSafeRender(PlanTabBodyImpl, "PlanTabBody");

export default PlanTabBody;
